import React, { memo, useMemo } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { CALENDAR_VIEW_TYPES } from 'constants/calendar';
import { DATE_FORMATS } from 'constants/index';

import { isWeekend } from 'utils/calendar';
import { createDate } from 'utils/date';

import classes from './DayCell.module.scss';

const DayCell = memo(({ viewType, cellDate, day, performedDates, startTime }) => {
  const isWeekendDay = useMemo(() => isWeekend(cellDate), [cellDate]);
  const matchingDate = useMemo(() => performedDates?.find(d => cellDate.isSame(createDate(d.startDate))), [
    performedDates,
    cellDate,
  ]);

  const isPast = useMemo(() => {
    if (!matchingDate?.startDate) {
      return false;
    }

    const performanceDate = createDate(matchingDate.startDate);
    const today = createDate();
    const isToday = performanceDate.format(DATE_FORMATS.DATE) === today.format(DATE_FORMATS.DATE);

    if (isToday) {
      return false;
    }

    return performanceDate < today;
  }, [matchingDate?.startDate]);

  // NOTE: Get the first and last performed dates for transparency logic
  const performedDateRange = useMemo(() => {
    if (!performedDates || performedDates.length === 0) {
      return { firstDate: null, lastDate: null };
    }

    const sortedDates = [...performedDates]
      .map(item => item.startDate)
      .sort((a, b) => createDate(a).valueOf() - createDate(b).valueOf());

    return {
      firstDate: createDate(sortedDates[0]),
      lastDate: createDate(sortedDates[sortedDates.length - 1]),
    };
  }, [performedDates]);

  // NOTE: Helper function to check if a date should be transparent
  const isDateTransparent = useMemo(() => {
    if (!performedDateRange.firstDate || !performedDateRange.lastDate) {
      return false;
    }

    return cellDate.isBefore(performedDateRange.firstDate) || cellDate.isAfter(performedDateRange.lastDate);
  }, [cellDate, performedDateRange]);

  const fifteenDay = viewType === CALENDAR_VIEW_TYPES.FIFTEEN_DAYS;

  const dayName = useMemo(
    () => (fifteenDay ? cellDate.format(DATE_FORMATS.SHORT_DAY) : cellDate.format(DATE_FORMATS.FULL_DAY).charAt(0)),
    [fifteenDay, cellDate],
  );

  return (
    <Typography
      size={10}
      variant="div"
      className={classnames(classes.monthDay, {
        [classes.monthDayWeekEnd]: isWeekendDay && !isDateTransparent,
        [classes.monthDayFuture]: !fifteenDay && matchingDate?.startDate && !isDateTransparent,
        [classes.monthDayPast]: !fifteenDay && isPast && !isDateTransparent,
        [classes.fifteenDay]: fifteenDay,
        [classes.fifteenDayPerformed]: fifteenDay && matchingDate?.startDate && !isPast && !isDateTransparent,
        [classes.fifteenDayPast]: fifteenDay && isPast && !isDateTransparent,
        [classes.monthDayTransparent]: !fifteenDay && isDateTransparent,
        [classes.fifteenDayTransparent]: fifteenDay && isDateTransparent,
        [classes.cancelled]: matchingDate?.isCancelled,
      })}
    >
      {matchingDate?.isCancelled && (
        <SpriteIcon
          icon="close"
          size={12}
          className={classnames(classes.cancelledIcon, {
            [classes.cancelledIconFifteen]: fifteenDay,
          })}
        />
      )}
      {matchingDate?.startDate && (
        <>
          <Typography
            className={classnames(classes.day, { [classes.dayFifteen]: fifteenDay })}
            {...(matchingDate?.isCancelled && { strikethrough: true })}
          >
            {String(day).padStart(2, '0')}
            <Typography className={classes.dayName}>{dayName}</Typography>
          </Typography>
          {fifteenDay && (
            <Typography
              size={10}
              weight="medium"
              className={classes.startTime}
              {...(matchingDate?.isCancelled && { strikethrough: true })}
            >
              {startTime || ' '}
            </Typography>
          )}
        </>
      )}
    </Typography>
  );
});

export default DayCell;
